import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TicketTable from "../../components/support/TicketTable";
import TicketFilters from "../../components/support/TicketFilters";
import TicketStatusBadge from "../../components/support/TicketStatusBadge";
import TicketPriorityBadge from "../../components/support/TicketPriorityBadge";
import { supportTicketService } from "../../services/supportTicketService";
import type { SupportTicket } from "../../types/supportTicket";
import { useFormat } from "../../context/FormatContext";
import "../../assets/supportTickets.css";

const SubscriberSupportTickets: React.FC = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    priority: "",
    department: "",
  });

  const { formatDateToDMY } = useFormat();

  const fetchTickets = async () => {
    try {
      setLoading(true);

      const res = await supportTicketService.getSubscriberTickets({
        page: 1,
        limit: 10,
        ...filters,
      });

      setTickets(res.tickets || []);
    } catch (error) {
      console.error("Failed to fetch subscriber tickets", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [filters]);

  const handleChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setFilters({
      search: "",
      status: "",
      priority: "",
      department: "",
    });
  };

  const getValue = (ticket: any, keys: string[], fallback = "-") => {
    for (const key of keys) {
      if (
        ticket?.[key] !== undefined &&
        ticket?.[key] !== null &&
        ticket?.[key] !== ""
      ) {
        return ticket[key];
      }
    }

    return fallback;
  };

  return (
    <>
      <div id="home-p" className="home-p pages-head9 text-center">
        <div className="container"></div>
      </div>

      <div className="support-page-wrap">
        <div className="support-card">
          <div className="support-card-header">
            <h2>
              <i className="fa fa-life-ring" /> Support
            </h2>

            <Link to="/support/create" className="open-ticket-btn">
              Open Support Ticket
            </Link>
          </div>

          <TicketFilters
            filters={filters}
            onChange={handleChange}
            onReset={handleReset}
          />

          {loading ? (
            <div className="ticket-empty-box">Loading tickets...</div>
          ) : (
            <>
              <div className="subscriber-ticket-desktop-table">
                <TicketTable
                  tickets={tickets}
                  detailBasePath="/support/tickets"
                />
              </div>

              <div className="subscriber-ticket-mobile-grid">
                {tickets.length === 0 ? (
                  <div className="ticket-empty-box">No tickets found.</div>
                ) : (
                  tickets.map((ticket: any) => {
                    const id = getValue(ticket, ["id", "ticket_id"]);
                    const ticketNo = getValue(ticket, [
                      "ticket_no",
                      "ticketNo",
                      "ticketNumber",
                      "ticket_number",
                    ]);
                    const subject = getValue(ticket, ["subject", "CoSubject"]);
                    const service = getValue(ticket, ["service", "CoService"]);
                    const department = getValue(ticket, [
                      "department",
                      "CoDepartment",
                    ]);
                    const status = getValue(ticket, [
                      "status",
                      "ticket_status",
                    ]);
                    const priority = getValue(ticket, ["priority"]);
                    const createdAt = getValue(ticket, [
                      "createdAt",
                      "created_at",
                    ]);
                    const updatedAt = getValue(ticket, [
                      "updatedAt",
                      "updated_at",
                    ]);

                    return (
                      <div className="subscriber-ticket-mobile-card" key={id}>
                        <div className="subscriber-ticket-mobile-top">
                          <div className="subscriber-ticket-status-line">
                            <TicketStatusBadge status={status} />
                          </div>
                          <div className="subscriber-ticket-mobile-info">
                            <p>
                              <span>Updated At</span>
                              <strong>{formatDateToDMY(updatedAt)}</strong>
                            </p>
                          </div>
                          <h3>{subject}</h3>
                        </div>

                        <p className="subscriber-ticket-number">
                          <strong>{ticketNo}</strong>
                        </p>

                        {service !== "-" ? (
                          <p className="subscriber-ticket-service">{service}</p>
                        ) : null}

                        <div className="subscriber-ticket-mobile-info">
                          <p>
                            <span>Department</span>
                            <strong>{department}</strong>
                          </p>

                          <p>
                            <span>Priority</span>
                            <TicketPriorityBadge priority={priority} />
                          </p>

                          <p>
                            <span>Created At</span>
                            <strong>{formatDateToDMY(createdAt)}</strong>
                          </p>
                        </div>

                        <Link
                          to={`/support/tickets/${id}`}
                          className="subscriber-ticket-view-btn"
                        >
                          View
                        </Link>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SubscriberSupportTickets;
